import React, { LegacyRef } from 'react';
import { Button } from '../../button';
import FormGroupValidation from '../shared/FormGroupValidation';
import ListboxItemsList from './ListboxItemsList';
import { IListOption, ListboxProps } from './types';
import { useFieldsetValidation } from '../shared/hooks/useFieldsetValidation';
import { inputDefaultProps } from '../../../props/inputProps';
import { noop } from '../../../helpers';
import { comparatorTypes, itemToString } from '../combobox/utility';
import { useElementIds } from '../../../helpers/hooks/useElementIds';

export const ListboxUnassignedLabel = 'Unassigned';
export const ListboxAssignedLabel = 'Assigned';
export const ListboxAllocateLabel = 'Allocate';
export const ListboxDeallocateLabel = 'Deallocate';
export const ListboxLoadingLabel = 'Loading...';

// https://www.w3.org/TR/2017/WD-wai-aria-practices-1.1-20170628/examples/listbox/listbox.html
const Listbox = React.forwardRef((props: ListboxProps, ref) => {
	const sortContentAscending = React.useCallback((first: string | undefined, second: string | undefined) => {
		if (first === undefined || second === undefined) {
			return 0;
		}

		const firstLowered = first?.toLowerCase();
		const secondLowered = second?.toLowerCase();
		if (firstLowered < secondLowered) {
			return -1;
		} else if (firstLowered > secondLowered) {
			return 1;
		}
		return 0;
	}, []);

	const {
		items = [],
		selectedItems = [],
		disabled = false,
		required = false,
		loading = false,
		label,
		assignedLabel = ListboxAssignedLabel,
		unassignedLabel = ListboxUnassignedLabel,
		allocateLabel = ListboxAllocateLabel,
		deallocateLabel = ListboxDeallocateLabel,
		loadingLabel = ListboxLoadingLabel,
		exclusionFilter = true,
		sortFn = sortContentAscending,
		compareType = comparatorTypes.startsWith,
		requiredFieldMessage = FormGroupValidation.defaultProps.requiredFieldMessage,
		suppressValidationMessage = false,
		type = 'responsive',
		'data-testid': testId,
		onAllocationChange = noop
	} = props;

	const { id } = useElementIds({
		prefix: 'listbox',
		id: props.id
	});

	const [selectedItemsLocal, setSelectedItemsLocal] = React.useState([...selectedItems]);
	const shouldOnAllocateFired = React.useRef(false); // to prevent fire on first render

	const { getGroupProps, getLabelProps, getHelpblockProps, inputRef, validation } = useFieldsetValidation({
		ref,
		label,
		required,
		...props
	});

	const splitItems = React.useCallback(
		(items: IListOption[], unassigned: IListOption[], assigned: IListOption[]): [IListOption[], IListOption[]] => {
			const hashedValues = Object.fromEntries(selectedItemsLocal.map(item => [item, true]));
			items.forEach(item => {
				const isGroup = item.value === undefined && item.items !== undefined;
				if (isGroup) {
					const [unassignedSub, assignedSub] = splitItems(item.items || [], [], []);
					if (unassignedSub.length > 0) {
						unassigned.push({ ...item, items: unassignedSub });
					}
					if (assignedSub.length > 0) {
						assigned.push({ ...item, items: assignedSub });
					}
				} else {
					if (item?.value !== undefined && hashedValues[item.value]) {
						assigned.push(item);
					} else {
						unassigned.push(item);
					}
				}
			});
			return [unassigned.sort((a, b) => sortFn(a.label, b.label)), assigned.sort((a, b) => sortFn(a.label, b.label))];
		},
		[sortFn, selectedItemsLocal]
	);
	const [unassigned, assigned] = React.useMemo(() => {
		return splitItems(items ?? [], [], []);
	}, [splitItems, items]);
	const [unassignedSelected, setUnassignedSelected] = React.useState<(string | number)[]>([]);
	const [assignedSelected, setAssignedSelected] = React.useState<(string | number)[]>([]);

	const areControlsVisible = (!loading && unassignedSelected.length > 0) || assignedSelected.length > 0;

	function moveToAssigned() {
		if (unassignedSelected.length === 0) {
			return;
		}
		setSelectedItemsLocal([...selectedItemsLocal, ...unassignedSelected]);
		setUnassignedSelected([]);
	}

	function moveToUnassigned() {
		if (assignedSelected.length === 0) {
			return;
		}
		const hashedValues = Object.fromEntries(assignedSelected.map(item => [item, true]));
		setSelectedItemsLocal(selectedItemsLocal.filter(item => !hashedValues[item]));
		setAssignedSelected([]);
	}

	const isRequired = validation.isRequired;
	const setIsInvalid = validation.setIsInvalid;
	React.useEffect(() => {
		// preventing from running on first render
		if (shouldOnAllocateFired.current) {
			onAllocationChange?.({ deallocated: unassigned, allocated: assigned });
			setIsInvalid(isRequired && assigned.length === 0);
		} else {
			shouldOnAllocateFired.current = true;
		}
	}, [setIsInvalid, isRequired, onAllocationChange, assigned, unassigned]);

	function getStringifiedValue(): string {
		function getValues(items: IListOption[]): (string | number)[] {
			return items.reduce((acc: (string | number)[], item: IListOption) => {
				if (item.items !== undefined) {
					acc = [...acc, ...getValues(item.items)];
				} else {
					if (item.value !== undefined) {
						acc.push(item.value);
					}
				}
				return acc;
			}, []);
		}

		return getValues(assigned).join('|');
	}

	// update form value on assign
	React.useEffect(() => {
		if (validation.formContext.setValue && props.name) {
			validation.formContext.setValue(props.name, assigned.map(selectedItem => itemToString(selectedItem, 'value')).join('|'));
		}
	}, [props.name, validation.formContext, assigned]);

	const legendProps = getLabelProps();

	const listboxClasses = ['listbox', type === 'responsive' && 'flex-column flex-md-row', type === 'vertical' && 'flex-column'].filter(Boolean).join(' ');

	const getControlsVisibilityClasses = (): string => {
		if (!areControlsVisible) {
			const visibilityByType = {
				vertical: 'd-none',
				horizontal: 'invisible',
				responsive: 'invisible d-none d-md-flex'
			};
			return visibilityByType[type];
		}
		return '';
	};

	const listboxControlsClasses = ['listbox-controls', 'd-flex gap-3 align-self-center', getControlsVisibilityClasses(), type].filter(Boolean).join(' ');

	return (
		<FormGroupValidation
			inputRef={inputRef}
			groupProps={getGroupProps({ controlId: undefined, disabled: loading || disabled, 'data-testid': testId })}
			labelProps={legendProps}
			helpblockProps={getHelpblockProps()}
			validation={validation}
			suppressValidationMessage={true}>
			<div className={listboxClasses}>
				<ListboxItemsList
					id={`${id}-unassigned`}
					label={unassignedLabel}
					parentLabelId={legendProps.id}
					items={unassigned}
					disabled={disabled}
					loading={loading}
					loadingLabel={loadingLabel}
					exclusionFilter={exclusionFilter}
					value={unassignedSelected}
					onChange={items => setUnassignedSelected(items.map(item => item.value ?? ''))}
					compareType={compareType}
					data-testid="unassigned-list"
				/>

				<div className={listboxControlsClasses}>
					<Button icon="next" variant="primary" data-testid="allocate-button" onClick={moveToAssigned} title={allocateLabel} />

					<Button icon="back" variant="neutral" data-testid="deallocate-button" onClick={moveToUnassigned} title={deallocateLabel} />
				</div>

				<ListboxItemsList
					id={`${id}-assigned`}
					label={assignedLabel}
					parentLabelId={legendProps.id}
					items={assigned}
					disabled={disabled}
					loading={loading}
					loadingLabel={loadingLabel}
					exclusionFilter={exclusionFilter}
					value={assignedSelected}
					onChange={items => setAssignedSelected(items.map(item => item.value ?? ''))}
					compareType={compareType}
					invalid={validation.isInvalid}
					parentLabel={label}
					requiredFieldMessage={requiredFieldMessage}
					suppressValidationMessage={suppressValidationMessage}
					data-testid="assigned-list"
				/>
			</div>

			<input ref={inputRef as unknown as LegacyRef<HTMLInputElement>} type="hidden" value={getStringifiedValue()} onChange={noop} />
		</FormGroupValidation>
	);
});
Listbox.displayName = 'Listbox';

Listbox.defaultProps = {
	...inputDefaultProps
};

export default Listbox;
